'use client';
import type { ReactNode } from 'react';
import { ButtonTab } from '@/app/components/shared/button-tab';
import { useRetentionStrategy } from '@/app/components/shared/RetentionStrategy/hooks/use-retention-strategy';
import type { PeriodType, TabItemType } from '@/app/types';
import { reportEvent } from '@/app/utils';

export const Tab = ({
  setPeriod,
  period,
  tabList,
}: {
  setPeriod: (val: PeriodType) => void;
  period: PeriodType;
  tabList: TabItemType[];
}) => {
  const { isEligible: isRetentionEligible } = useRetentionStrategy();

  const renderTooltip = (item: TabItemType): ReactNode => {
    if (!item.tooltip) return null;

    const isSubscriptionMonthlyTab = item.value === 'subscription_monthly';

    if (isRetentionEligible && isSubscriptionMonthlyTab && item.isDiscount) {
      return (
        <div className="flex h-[25px] items-center rounded-8 border border-white/30 bg-gradient-to-r from-[#b2d7ff] to-[#ffe3da] px-2 font-medium text-system-black text-xs uppercase italic shadow-[inset_0px_0px_6px_0px_white] sm:hidden">
          {item.tooltip}
        </div>
      );
    }

    return (
      <div className="flex h-[25px] items-center rounded-8 bg-[linear-gradient(90deg,#5765F2_0%,#808BFF_100%)] px-2 font-medium text-system-white text-xs uppercase italic sm:hidden">
        {item.tooltip}
      </div>
    );
  };

  return (
    <ButtonTab
      className="relative my-6 flex h-11 justify-center rounded-[12px] bg-system-white08 p-0 font-normal ring-1 ring-system-white08 sm:mx-4 sm:my-4 sm:w-auto"
      buttonClassName="relative min-w-[200px] rounded-[12px] border border-transparent px-10 py-3 text-sm text-system-white64 gap-2 overflow-hidden aria-selected:text-system-white aria-selected:bg-system-white24 aria-selected:border-system-white08 sm:w-full sm:min-w-0 sm:flex-wrap sm:min-h-[44px] sm:px-3 opacity-100 hover:text-system-white h-11"
      active={period}
      onChange={(val) => {
        const nextPeriod = val as PeriodType;
        if (isRetentionEligible && nextPeriod !== period) {
          reportEvent('subscription_reactivation_cancel_click', { pricing: 'switch_tab' });
        }
        setPeriod(nextPeriod);
      }}
      options={tabList.map((item) => ({
        value: item.value,
        label: (
          <>
            <span className="truncate">{item.label}</span>
            {renderTooltip(item)}
          </>
        ),
      }))}
    />
  );
};
